<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Card } from '@drama/ui'

import { api } from '../api.js'
import ListToolbar from '../components/ListToolbar.vue'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const disputes = ref(null)
const contests = ref([])
const error = ref('')
const view = useListView(disputes, {
  text: (dispute) => `${dispute.task.title}\n${dispute.task.spec}`,
  sorts: [{ value: 'newest', label: t('channels.sortNewest'), compare: (a, b) => b.created_at.localeCompare(a.created_at) }, { value: 'oldest', label: t('list.oldest'), compare: (a, b) => a.created_at.localeCompare(b.created_at) }, { value: 'reward', label: t('board.sorts.reward'), compare: (a, b) => b.task.reward - a.task.reward }],
})

async function load() {
  const queue = await api('GET', '/api/voko/admin/disputes')
  disputes.value = queue.disputes
  contests.value = queue.platform_work.contests
}

/**
 * @param {string} taskId
 * @param {'settled'|'refunded'} outcome
 */
async function resolve(taskId, outcome) {
  error.value = ''
  try {
    await api('POST', `/api/voko/admin/disputes/${taskId}/resolve`, { outcome })
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  try {
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
})
</script>

<template>
  <h1>{{ t('adminDisputes.title') }}</h1>
  <p>{{ t('adminDisputes.intro') }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <template v-if="disputes">
    <p v-if="disputes.length === 0">{{ t('adminDisputes.empty') }}</p>
    <ListToolbar v-if="disputes.length > 0" :view="view" />
    <p v-if="disputes.length > 0 && view.shown.value.length === 0">{{ t('list.noMatch') }}</p>
    <Card v-for="dispute in view.shown.value" :key="dispute.dispute_id" class="voko-card">
      <h2>{{ dispute.task.title }}</h2>
      <p>{{ t(`board.templates.${dispute.task.template}`) }} · {{ t('board.reward', { reward: dispute.task.reward }) }} · {{ new Date(dispute.created_at).toLocaleString() }}</p>
      <p class="voko-pre">{{ dispute.task.spec }}</p>
      <p v-if="dispute.ai_proposal" class="voko-pre"><strong>{{ t('adminDisputes.ai', { recommendation: t(`adminDisputes.recommendations.${dispute.ai_proposal.recommendation}`) }) }}</strong> {{ dispute.ai_proposal.reason }}</p>
      <p v-else>{{ t('adminDisputes.aiPending') }}</p>
      <p v-if="dispute.overdue" role="alert">{{ t('adminDisputes.overdue') }}</p>
      <div class="voko-actions">
        <Button @click="resolve(dispute.task.id, 'settled')">{{ t('adminDisputes.pay') }}</Button>
        <Button variant="secondary" @click="resolve(dispute.task.id, 'refunded')">{{ t('adminDisputes.refund') }}</Button>
      </div>
    </Card>
    <template v-if="contests.length > 0">
      <h2>{{ t('adminDisputes.contestsTitle') }}</h2>
      <Card v-for="contest in contests" :key="contest.task_id" class="voko-card">
        <p>{{ contest.task_id }} · {{ new Date(contest.due_at).toLocaleString() }}</p>
        <p v-if="contest.overdue" role="alert">{{ t('adminDisputes.contestOverdue') }}</p>
      </Card>
    </template>
  </template>
</template>

<style scoped>
.voko-card {
  margin-bottom: var(--drama-space-md);
}
.voko-pre {
  white-space: pre-wrap;
}
.voko-actions {
  display: flex;
  gap: var(--drama-space-sm);
}
</style>
